import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Eye, Settings, Trash2, Server, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import DataTable from '../components/common/DataTable';
import type { Column } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import StatCard from '../components/common/StatCard';
import ConnectorModal from '../components/connectors/ConnectorModal';
import type { ConnectorFormData } from '../components/connectors/ConnectorModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/common/ToastContainer';
import connectorService from '../services/connector.service';
import type { Connector } from '../services/connector.service';
import { useTenantStore } from '../stores/tenantStore';
import { useAuthStore } from '../stores/authStore';
import { formatDate } from '../utils/formatDate';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissions } from '../hooks/usePermissions';
import { USER_TYPES } from '../utils/constants';

const Connectors = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [loading, setLoading] = useState(false);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingConnector, setEditingConnector] = useState<Connector | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; connector?: Connector }>({ show: false });
  const [deleting, setDeleting] = useState(false);

  const { canCreate, canUpdate, canDelete } = usePermissions();
  const { getTenantId } = useTenantStore();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  // TenantAdmin operates on their own tenant; SuperAdmin and ChannelAdmin have
  // no tenant of their own and operate on the topbar selection. The old
  // `isSuperAdmin ? topbar : user.tenantId` split gave a ChannelAdmin an empty
  // tenant, so nothing loaded and create was scoped to nowhere.
  const tenantId = user?.tenantId || getTenantId() || '';

  const fetchConnectors = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (tenantId) params.tenantId = tenantId;
      const response = await connectorService.list(params);
      setConnectors(response.items || []);
      setTotalCount(response.total || 0);
    } catch {
      // Silently handle - list will show empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectors();
  }, [tenantId]);

  const activeConnectors = connectors.filter(c => c.status !== 'pending_approval' && c.status !== 'decommissioned');
  const pendingConnectors = connectors.filter(c => c.status === 'pending_approval');

  // Derive online/offline from wireguard tunnel status
  const isConnectorOnline = (c: Connector) => (c as any).wireguard_status === 'active';
  const onlineCount = activeConnectors.filter(isConnectorOnline).length;
  const offlineCount = activeConnectors.filter(c => !isConnectorOnline(c)).length;

  const getDisplayStatus = (connector: Connector): string => {
    // Lifecycle states shown as-is
    if (['pending_approval', 'approved', 'provisioned', 'decommissioned'].includes(connector.status)) {
      const labels: Record<string, string> = {
        pending_approval: 'Pending',
        approved: 'Approved',
        provisioned: 'Provisioned',
        decommissioned: 'Decommissioned',
      };
      return labels[connector.status] || connector.status;
    }
    // For active/offline — use live telemetry
    return isConnectorOnline(connector) ? 'Online' : 'Offline';
  };

  const syncStatusMap: Record<string, string> = {
    synced: 'Synced',
    pending: 'Pending',
    applying: 'Applying',
    failed: 'Failed',
    unknown: 'Unknown',
  };

  const columns: Column<Connector>[] = [
    {
      key: 'connector_name',
      label: 'Name',
      sortable: true,
      render: (connector) => (
        <button
          onClick={() => navigate(`/connectors/${connector.id}`)}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Server className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{connector.connector_name}</span>
        </button>
      ),
    },
    {
      key: 'deployment_region',
      label: 'Region',
      sortable: true,
      render: (connector) => (
        connector.deployment_region ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
            {connector.deployment_region}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (connector) => <StatusBadge status={getDisplayStatus(connector)} />,
    },
    {
      key: 'wan_ip',
      label: 'WAN IP',
      render: (connector) => <span>{connector.wan_ip || '-'}</span>,
    },
    {
      key: 'agent_version',
      label: 'Version',
      render: (connector) => <span className="font-mono text-xs">{connector.agent_version || '-'}</span>,
    },
    {
      key: 'hub_edge_name',
      label: 'Hub Edge',
      render: (connector) => connector.hub_edge_name ? (
        <button
          onClick={() => navigate(
            connector.hub_type === 'mtge'
              ? `/mtge/${connector.hub_mtge_id}`
              : `/edges/${connector.hub_edge_id}`
          )}
          className="text-primary-600 hover:text-primary-700 hover:underline text-sm"
        >
          {connector.hub_edge_name}{connector.hub_type === 'mtge' ? ' (MTGE)' : ''}
        </button>
      ) : (
        <span className="text-gray-400">-</span>
      ),
    },
    {
      key: 'last_seen',
      label: 'Last Seen',
      sortable: true,
      render: (connector) => {
        if (!connector.last_seen) return <span>-</span>;
        return <span>{formatDate(connector.last_seen, useSettingsStore.getState().getDateFormatSettings())}</span>;
      },
    },
    {
      key: 'sync_status',
      label: 'Sync Status',
      sortable: true,
      render: (connector) => <StatusBadge status={syncStatusMap[connector.sync_status] || connector.sync_status} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (connector) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => navigate(`/connectors/${connector.id}`)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          {canUpdate && (
            <button
              onClick={() => setEditingConnector(connector)}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Edit"
            >
              <Settings className="w-4 h-4 text-gray-600 hover:text-primary-600" />
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm({ show: true, connector })}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const pendingColumns: Column<Connector>[] = [
    {
      key: 'connector_name',
      label: 'Name',
      render: (connector) => <span className="font-medium">{connector.connector_name}</span>,
    },
    {
      key: 'serial_number',
      label: 'Serial Number',
    },
    {
      key: 'deployment_region',
      label: 'Region',
      render: (connector) => (
        connector.deployment_region ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
            {connector.deployment_region}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
    {
      key: 'registered_at',
      label: 'Registered',
      render: (connector) => {
        if (!connector.registered_at) return <span>-</span>;
        return <span>{formatDate(connector.registered_at, useSettingsStore.getState().getDateFormatSettings())}</span>;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (connector) => (
        <div className="flex items-center justify-end space-x-2">
          {canUpdate && (
            <button
              onClick={() => handleApprove(connector)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
            >
              Approve
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteConfirm({ show: true, connector })}
              className="p-1.5 hover:bg-red-50 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleSaveConnector = async (data: ConnectorFormData) => {
    try {
      if (editingConnector) {
        await connectorService.update(editingConnector.id, {
          connectorName: data.connectorName,
          description: data.description,
          wanIp: data.wanIp,
          hubEdgeId: data.hubEdgeId || undefined,
          hubType: data.hubType,
          deploymentRegion: data.deploymentRegion,
        });
        toast.success('Connector Updated', 'Connector has been updated');
        await fetchConnectors();
        setEditingConnector(undefined);
        return;
      }

      // Create — return the result so the modal can show credentials
      const result = await connectorService.create({
        connectorName: data.connectorName,
        tenantId: data.tenantId,
        description: data.description,
        deploymentRegion: data.deploymentRegion,
        wanIp: data.wanIp,
        hubEdgeId: data.hubEdgeId || undefined,
        hubType: data.hubType,
        autoApprove: data.autoApprove,
      });
      toast.success('Connector Created', 'Connector has been registered');
      await fetchConnectors();
      return result;
    } catch (error: any) {
      toast.error('Operation Failed', error.message || 'Failed to save connector');
      throw error;
    }
  };

  const handleApprove = async (connector: Connector) => {
    try {
      await connectorService.approve(connector.id);
      toast.success('Connector Approved', `"${connector.connector_name}" has been approved`);
      await fetchConnectors();
    } catch (error: any) {
      toast.error('Approval Failed', error.message || 'Failed to approve connector');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.connector) return;
    try {
      setDeleting(true);
      await connectorService.decommission(deleteConfirm.connector.id);
      toast.success('Connector Deleted', `"${deleteConfirm.connector.connector_name}" has been deleted`);
      await fetchConnectors();
      setDeleteConfirm({ show: false });
    } catch (error: any) {
      toast.error('Delete Failed', error.message || 'Failed to delete connector');
    } finally {
      setDeleting(false);
    }
  };

  const canAddConnector = !!tenantId;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Connectors</h1>
          <p className="text-gray-600 mt-1">Manage connector devices</p>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={fetchConnectors} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {canCreate && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              disabled={!canAddConnector}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canAddConnector ? 'Select a tenant first' : ''}
            >
              <Plus className="w-4 h-4" />
              <span>Add Connector</span>
            </button>
          )}
        </div>
      </div>

      {isSuperAdmin && !tenantId && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <strong>Select a tenant from the header dropdown</strong> to manage connectors.
          Hubs are tenant-scoped — MTGE shared gateways only appear in the Add
          Connector dialog when a tenant has an active assignment on that MTGE.
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('active')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'active'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Active Connectors
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center space-x-2 ${
                activeTab === 'pending'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>Pending Approval</span>
              {pendingConnectors.length > 0 && (
                <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none rounded-full ${
                  activeTab === 'pending' ? 'bg-primary-100 text-primary-700' : 'bg-orange-100 text-orange-700 animate-pulse'
                }`}>
                  {pendingConnectors.length}
                </span>
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Metrics */}
      {activeTab === 'active' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard icon={Server} title="Total" value={totalCount} color="primary" />
          <StatCard icon={CheckCircle} title="Online" value={onlineCount} color="success" />
          <StatCard icon={AlertCircle} title="Offline" value={offlineCount} color="secondary" />
          <StatCard icon={Clock} title="Pending" value={pendingConnectors.length} color="primary" />
        </div>
      )}

      {/* Content */}
      {activeTab === 'active' ? (
        <div className="card">
          <DataTable
            columns={columns}
            data={activeConnectors}
            searchPlaceholder="Search connectors by name or serial..."
            loading={loading}
          />
        </div>
      ) : (
        <div className="card">
          <DataTable
            columns={pendingColumns}
            data={pendingConnectors}
            searchPlaceholder="Search pending connectors..."
            loading={loading}
          />
        </div>
      )}

      {/* Modals */}
      <ConnectorModal
        isOpen={isAddModalOpen}
        onClose={() => { setIsAddModalOpen(false); fetchConnectors(); }}
        onSave={handleSaveConnector}
        tenantId={tenantId}
        mode="add"
      />
      <ConnectorModal
        isOpen={!!editingConnector}
        onClose={() => setEditingConnector(undefined)}
        onSave={handleSaveConnector}
        tenantId={editingConnector?.tenant_id || tenantId}
        connector={editingConnector ? {
          id: editingConnector.id,
          connectorName: editingConnector.connector_name,
          tenantId: editingConnector.tenant_id,
          description: editingConnector.description || '',
          deploymentRegion: editingConnector.deployment_region || '',
          wanIp: editingConnector.wan_ip || '',
          hubEdgeId: editingConnector.hub_type === 'mtge' ? (editingConnector.hub_mtge_id || '') : (editingConnector.hub_edge_id || ''),
          hubType: editingConnector.hub_type || 'dedicated',
          autoApprove: false,
        } : undefined}
        mode="edit"
      />
      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete Connector"
        message={`Are you sure you want to delete "${deleteConfirm.connector?.connector_name}"? This will remove all associated configuration including hub peering and E2E peering memberships. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deleting}
      />
    </div>
  );
};

export default Connectors;
